/**
 * pmo#1751 / GH#2107 — el paso 2 del alta pide razón social, país, RNC, dirección fiscal, ciudad,
 * provincia y teléfono comercial, y hasta hoy los tiraba. Este módulo los lleva desde el alta hasta
 * `POST /auth/complete-onboarding` (que sí los guarda, #2117).
 *
 * Reglas (letra de la CTO en GH#2107):
 *  - El PLAN y el PAGO NO viajan. Nada los lee: no se guardan.
 *  - Solo el dueño los carga en su primer onboarding: el payload se ata al correo del alta y solo
 *    se usa si el usuario logueado es ese; un invitado nunca lo manda (el backend da 400
 *    FISCAL_SOLO_DUENO).
 *  - Puro y con el `Storage` inyectado, para poder probarlo.
 */
import type { SignupCompanyData } from '../types';

export const CLAVE_FISCAL_ALTA = 'signup_fiscal_payload';
/** El alta dejó datos fiscales que no se pudieron guardar: hay que cargarlos en Configuración. */
export const CLAVE_FISCAL_PENDIENTE = 'signup_fiscal_pendiente';

/** Los nombres y límites son los de `DatosFiscalesOnboardingDto` (apps/api, #2117). */
export interface FiscalOnboarding {
  razonSocial: string;
  pais: string;
  rnc?: string;
  direccionFiscal: string;
  ciudad: string;
  provincia: string;
  telefonoComercial?: string;
}

export const LIMITES_FISCAL = {
  razonSocial: 200,
  direccionFiscal: 200,
  ciudad: 50,
  provincia: 50,
  telefonoComercial: 30,
} as const;

/** Lo que el backend recibe: recortado y sin vacíos (un RNC vacío en otro país no se manda). */
export function construirFiscal(c: SignupCompanyData): FiscalOnboarding {
  const fiscal: FiscalOnboarding = {
    razonSocial: c.razonSocial.trim(),
    pais: c.pais,
    direccionFiscal: c.direccionFiscal.trim(),
    ciudad: c.ciudad.trim(),
    provincia: c.provincia.trim(),
  };
  const rnc = c.rnc?.trim();
  if (rnc) fiscal.rnc = rnc;
  const tel = c.telefonoComercial?.trim();
  if (tel) fiscal.telefonoComercial = tel;
  return fiscal;
}

type Escribible = Pick<Storage, 'setItem'>;
type Legible = Pick<Storage, 'getItem'>;
type Borrable = Pick<Storage, 'removeItem'>;

const mismoCorreo = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export function guardarFiscalDelAlta(storage: Escribible, correo: string, company: SignupCompanyData): void {
  try {
    storage.setItem(CLAVE_FISCAL_ALTA, JSON.stringify({ correo, fiscal: construirFiscal(company) }));
  } catch {
    /* sin storage (modo privado): el alta sigue; el RNC se carga después en Configuración */
  }
}

/** El fiscal del alta, solo si el usuario logueado es quien se dio de alta. Si no, null. */
export function leerFiscalDelAlta(storage: Legible, correoLogueado?: string | null): FiscalOnboarding | null {
  try {
    const crudo = storage.getItem(CLAVE_FISCAL_ALTA);
    if (!crudo) return null;
    const { correo, fiscal } = JSON.parse(crudo) as { correo?: string; fiscal?: FiscalOnboarding };
    if (!mismoCorreo(correo, correoLogueado)) return null;
    if (!fiscal || typeof fiscal !== 'object') return null;
    const { razonSocial, pais, direccionFiscal, ciudad, provincia } = fiscal;
    if (![razonSocial, pais, direccionFiscal, ciudad, provincia].every((v) => typeof v === 'string' && v)) return null;
    return fiscal;
  } catch {
    return null;
  }
}

export function borrarFiscalDelAlta(storage: Borrable): void {
  try {
    storage.removeItem(CLAVE_FISCAL_ALTA);
  } catch {
    /* noop */
  }
}

const normalizarCorreo = (c?: string | null) => (c ?? '').trim().toLowerCase();

/** La marca va atada al correo del alta, igual que el payload: otro usuario del mismo navegador no la ve. */
export function marcarFiscalPendiente(correo: string | null | undefined, ...storages: Escribible[]): void {
  const valor = normalizarCorreo(correo);
  if (!valor) return; // sin correo no hay a quién atarla: no se marca (el aviso nunca sale en falso)
  for (const storage of storages) {
    try {
      storage.setItem(CLAVE_FISCAL_PENDIENTE, valor);
    } catch {
      /* noop */
    }
  }
}

/**
 * ¿Hay una marca de «fiscal pendiente» en alguno de los almacenes? La escribe el saga cuando el backend
 * rechazó los datos fiscales del alta. NUNCA decide por sí sola si el aviso se muestra: la decide el dato
 * del backend (ver `decidirAvisoFiscal`).
 */
export function hayFiscalPendiente(correo: string | null | undefined, ...storages: Legible[]): boolean {
  const valor = normalizarCorreo(correo);
  if (!valor) return false;
  return storages.some((s) => {
    try {
      return s.getItem(CLAVE_FISCAL_PENDIENTE) === valor;
    } catch {
      return false;
    }
  });
}

export function borrarFiscalPendiente(...storages: Borrable[]): void {
  for (const storage of storages) {
    try {
      storage.removeItem(CLAVE_FISCAL_PENDIENTE);
    } catch {
      /* noop */
    }
  }
}
